/* global settleComboQuantity, parseCartApiError, settleWholeCases, casesAvailableMessage, changeCartLine, fetchCart */

// Fetches transit time per warehouse (one request per ID, shared across
// cards). Caches in sessionStorage. Falls back to static text on failure.

const WAREHOUSE_API_BASE = 'https://165.227.191.53.nip.io';
const WAREHOUSE_CACHE_PREFIX = 'volumeCard:warehouseTransit:';
const activeWarehouseFetches = new Map();

function buildWarehouseUrl(warehouseId) {
  return `${WAREHOUSE_API_BASE}/api/public/locations/by-shipperhq-id/${encodeURIComponent(warehouseId)}/ships-from`;
}

function fetchWarehouseTransitTime(warehouseId) {
  if (!warehouseId) return Promise.resolve(null);

  // Check session cache first.
  try {
    const cached = sessionStorage.getItem(WAREHOUSE_CACHE_PREFIX + warehouseId);
    if (cached !== null) return Promise.resolve(cached || null);
  } catch (e) {
    // sessionStorage may be unavailable (private browsing); continue to fetch.
  }

  // Reuse the in-progress fetch for this warehouse if one already exists.
  if (activeWarehouseFetches.has(warehouseId)) {
    return activeWarehouseFetches.get(warehouseId);
  }

  const promise = fetch(buildWarehouseUrl(warehouseId))
    .then((response) => {
      if (!response.ok) return null;
      return response.json();
    })
    .then((data) => {
      const transitTime = data && typeof data.transit_time === 'string' && data.transit_time.length > 0
        ? data.transit_time
        : null;
      try {
        sessionStorage.setItem(WAREHOUSE_CACHE_PREFIX + warehouseId, transitTime || '');
      } catch (e) {
        // Ignore storage errors.
      }
      return transitTime;
    })
    .catch(() => null)
    .finally(() => {
      activeWarehouseFetches.delete(warehouseId);
    });

  activeWarehouseFetches.set(warehouseId, promise);
  return promise;
}

// Units of a variant the cart holds across every line, assortment components included. Matches
// item_count_for_variant, which Liquid renders the first cap from.
function unitsInCartForVariant(cart, variantId) {
  return (cart?.items || [])
    .filter((item) => item.variant_id === Number(variantId))
    .reduce((total, item) => total + item.quantity, 0);
}

// Stock a variant turned out to hold, learned when the cart refused part of an add: it takes every
// unit it can before refusing, so what it then holds is all there is. Kept per variant rather than
// per card, so every card for that variant stops offering what the cart just refused.
const revealedStockUnits = new Map();

// Set once the cart changes on this page, so a card that initialises later doesn't trust the cap
// Liquid rendered at page load.
let cartChangedSinceLoad = false;

let caseCapRefresh = null;
let caseCapRefreshQueued = false;

// Re-reads the cart once for every card with a stock cap, however many cards or events asked. An
// event arriving mid-fetch queues one more pass, since the cart may have changed after it went out.
function refreshCaseCaps() {
  if (caseCapRefresh) {
    caseCapRefreshQueued = true;
    return caseCapRefresh;
  }

  caseCapRefresh = (async () => {
    do {
      caseCapRefreshQueued = false;
      // eslint-disable-next-line no-await-in-loop
      const cart = await fetchCart();
      document.querySelectorAll('product-card-volume').forEach((card) => card.applyCaseCap(cart));
    } while (caseCapRefreshQueued);
  })()
    .catch((error) => console.error(error))
    .finally(() => { caseCapRefresh = null; });

  return caseCapRefresh;
}

// Every cart change, not only this variant's: assortment and other adds can hold the same variant on
// lines of their own. An error is included because a refusal can still have changed the cart.
['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((eventName) => {
  document.addEventListener(eventName, () => {
    cartChangedSinceLoad = true;
    if (document.querySelector('product-card-volume [data-stock-units]')) refreshCaseCaps();
  });
});

class ProductCardVolume extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.setupRefs();
    this.relocateModal();
    this.localeStrings = window.theme.volumeCardStrings || {};
    this.bindEvents();
    // Initial state: highlight the active tier for default qty (1 case)
    this.handleQtyChange();
    this.updateShipTiming();
    if (cartChangedSinceLoad && this.stockUnits !== null) refreshCaseCaps();
  }

  // Read static config from the card root and cache DOM references.
  setupRefs() {
    this.itemsPerCase = parseInt(this.dataset.itemsPerCase, 10) || 1;
    this.baseUnitPrice = parseFloat(this.dataset.unitPrice) || 0;
    this.baseRegularPrice = parseFloat(this.dataset.regularUnitPrice) || this.baseUnitPrice; // Only differs from the unit price on a product with no tiers and a compare-at price above it.
    this.hasTiers = this.dataset.hasTiers === 'true';
    this.isAssortment = this.dataset.isAssortment === 'true';
    this.isComboCase = this.dataset.isComboCase === 'true';
    this.uomSingular = this.dataset.uomSingular;
    this.uomPlural = this.dataset.uomPlural;

    // Two footers exist in the DOM (grid + list); query all and sync them.
    this.qtyInputs = this.querySelectorAll('[data-card-qty-input]');
    this.sublabels = this.querySelectorAll('[data-qty-input-sublabel]');
    this.qtyCalcBlocks = this.querySelectorAll('[data-qty-calc]');
    this.tierRows = this.querySelectorAll('[data-tier]');
    this.atcBtns = this.querySelectorAll('[data-action="add-to-cart"]');
    this.assortmentBtns = this.querySelectorAll('[data-action="open-assortment-drawer"]');
    this.assortmentWarnings = this.querySelectorAll('[data-assortment-in-cart-warning]');
    this.casesInCartWarnings = this.querySelectorAll('[data-cases-in-cart-warning]');
    this.shipTimingModalTrigger = this.querySelector('[data-ship-timing-modal-trigger]');
    this.shipTimingModal = this.querySelector('modal-dialog');
    this.warehouseId = this.dataset.warehouseId || '';
    this.shipTimingTextEls = this.querySelectorAll('[data-ship-timing-text]');
  }

  // Move the ship timing modal out of the carousel/card overflow context to document.body
  // so it can escape ancestor overflow:hidden boundaries when opened.
  relocateModal() {
    if (this.shipTimingModal) {
      document.body.appendChild(this.shipTimingModal);
    }
  }

  // Swap the static fallback for the warehouse's real transit time when it
  // loads. Failures keep the fallback in place.
  updateShipTiming() {
    if (!this.warehouseId || !this.shipTimingTextEls.length) return;
    const template = (this.localeStrings && this.localeStrings.shipTimingTemplate) || '';
    if (!template) return;

    fetchWarehouseTransitTime(this.warehouseId).then((transitTime) => {
      if (!transitTime) return;
      const text = template.replace('[transit_time]', transitTime);
      this.shipTimingTextEls.forEach((el) => {
        el.textContent = text;
      });
    });
  }

  bindEvents() {
    this.qtyInputs.forEach((input) => {
      input.addEventListener('change', this.handleQtyChange.bind(this));
    });
    this.atcBtns.forEach((btn) => {
      btn.addEventListener('click', this.handleAtcClick.bind(this));
    });
    this.assortmentBtns.forEach((btn) => {
      btn.addEventListener('click', this.handleAssortmentClick.bind(this));
    });
    this.querySelectorAll('[data-assortment-in-cart-warning] .js-close-warning').forEach((btn) => {
      btn.addEventListener('click', this.handleWarningCloseClick.bind(this));
    });
    if (this.shipTimingModalTrigger) {
      this.shipTimingModalTrigger.addEventListener('click', this.handleShipTimingModalOpen.bind(this));
    }
    if (this.isAssortment && this.dataset.productUrl) {
      this.cartChangeHandler = this.syncAssortmentCartState.bind(this);
      document.addEventListener('on:cart:add', this.cartChangeHandler);
      document.addEventListener('on:line-item:change', this.cartChangeHandler);
    }
  }

  disconnectedCallback() {
    if (!this.cartChangeHandler) return;
    document.removeEventListener('on:cart:add', this.cartChangeHandler);
    document.removeEventListener('on:line-item:change', this.cartChangeHandler);
  }
  
  // Units a case holds, never below one, so dividing units into cases is always safe.
  get caseUnits() {
    return this.itemsPerCase > 1 ? this.itemsPerCase : 1;
  }

  // Case ceiling the stepper may not pass, set by Liquid from the stock left after the cart.
  // Null where there is nothing to cap: an untracked variant, one that sells past zero, or an
  // assortment, whose components are capped in the drawer instead.
  get maxCases() {
    const input = this.qtyInputs[0];
    if (!input || input.max === '') return null;
    const parsed = parseInt(input.max, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Stock the case cap is worked out from, in units, or null where the card has no cap: an
  // untracked variant, one that sells past zero, an assortment or a combo case. Liquid only renders
  // data-stock-units where it renders a cap.
  get stockUnits() {
    const input = this.qtyInputs?.[0];
    if (!input || input.dataset.stockUnits === undefined) return null;

    const variantId = Number(input.dataset.variantId);
    if (revealedStockUnits.has(variantId)) return revealedStockUnits.get(variantId);

    const parsed = parseInt(input.dataset.stockUnits, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Records what a refused add showed this variant's stock to be, for every card of it to cap from.
  revealStock(cart) {
    const input = this.qtyInputs[0];
    if (!input || input.dataset.stockUnits === undefined || !cart) return;

    revealedStockUnits.set(
      Number(input.dataset.variantId),
      unitsInCartForVariant(cart, input.dataset.variantId)
    );
  }

  // Works the case cap out again from stock less what the cart holds, so it follows the cart both
  // ways: an add takes cases off it, and lowering or removing the line in the cart gives them back.
  applyCaseCap(cart) {
    const { stockUnits } = this;
    if (stockUnits === null) return;

    const unitsInCart = unitsInCartForVariant(cart, this.qtyInputs[0].dataset.variantId);
    const remaining = Math.floor((stockUnits - unitsInCart) / this.caseUnits);

    if (remaining < 1) {
      this.setCasesExhausted(true, unitsInCart > 0);
      return;
    }

    this.qtyInputs.forEach((input) => { input.max = remaining; });
    this.setCasesExhausted(false);
    this.handleQtyChange();
  }

  // Matches what Liquid renders below one case. The ceiling comes off rather than going to zero, so
  // the card keeps showing a case's worth of pricing while its controls go dead. Where the shopper's
  // own cart took the stock, the button keeps its label and the panel says why, since the cases
  // exist and are just already taken; otherwise the button reads sold out. Turned off again when the
  // cart gives cases back.
  setCasesExhausted(exhausted, cartHolds = false) {
    this.qtyInputs.forEach((input) => {
      if (exhausted) input.removeAttribute('max');
      input.disabled = exhausted;

      const qtyWrap = input.closest('.qty-input');
      if (!qtyWrap) return;
      qtyWrap.classList.toggle('qty-input--disabled', exhausted);
      qtyWrap.querySelectorAll('.qty-input__btn').forEach((btn) => { btn.disabled = exhausted; });
    });

    this.atcBtns.forEach((btn) => {
      // Not-for-sale and preorder buttons carry no labels: their state isn't the stock's to set.
      const { labelAdd, labelSoldOut } = btn.dataset;
      if (!labelAdd) return;

      btn.disabled = exhausted;
      const text = btn.querySelector('.card__cta-text');
      if (text) text.textContent = exhausted && !cartHolds ? labelSoldOut : labelAdd;
    });

    this.casesInCartWarnings.forEach((warning) => { warning.hidden = !(exhausted && cartHolds); });
  }

  handleQtyChange(evt) {
    // Read from the input that was changed; fall back to the first.
    const sourceInput = evt && evt.currentTarget ? evt.currentTarget : this.qtyInputs[0];
    // The buttons step within the input's range, but a typed value is bound by nothing, so it is
    // brought back into range here before anything is priced from it.
    const { maxCases } = this;
    let cases = parseInt(sourceInput.value, 10) || 1;
    if (cases < 1) cases = 1;
    if (maxCases !== null && cases > maxCases) cases = maxCases;
    const totalUnits = cases * this.itemsPerCase;

    // Sync the other qty inputs, and let the plus button go dead at the ceiling rather than
    // looking live and doing nothing.
    this.qtyInputs.forEach((input) => {
      if (input.value !== String(cases)) input.value = cases;

      const qtyWrap = input.closest('.qty-input');
      const plusBtn = qtyWrap ? qtyWrap.querySelector('.btn--plus') : null;
      // An assortment in the cart is edited from the cart, which disables the whole stepper.
      if (plusBtn && !input.disabled) {
        plusBtn.disabled = maxCases !== null && cases >= maxCases;
      }
    });
  
    // Find the highest tier whose min-cases threshold is met
    let activeUnitPrice = this.baseUnitPrice;
    let activeRegularPrice = this.baseRegularPrice;
    let activeTierIndex = 0;
    this.tierRows.forEach((row) => {
      const minCases = parseInt(row.dataset.minCases, 10);
      if (cases >= minCases) {
        activeUnitPrice = parseFloat(row.dataset.unitPrice);
        activeRegularPrice = parseFloat(row.dataset.regularPrice) || activeUnitPrice;
        activeTierIndex = parseInt(row.dataset.tier, 10);
      }
    });
  
    const totalPrice = totalUnits * activeUnitPrice;
  
    // Update all sublabels (case / cases).
    this.sublabels.forEach((sublabel) => {
      const labelOne = sublabel.dataset.labelOne;
      const labelOther = sublabel.dataset.labelOther;
      sublabel.textContent = cases === 1 ? labelOne : labelOther;
    });
  
    // Update both desktop and mobile qty calc blocks
    this.qtyCalcBlocks.forEach((block) => {
      const unitsEl = block.querySelector('[data-qty-calc-units]');
      const unitPriceEl = block.querySelector('[data-qty-calc-unit-price]');
      const regularPriceEl = block.querySelector('[data-qty-calc-regular-price]');
      const priceEl = block.querySelector('[data-card-price]');
    
      if (unitsEl) {
        const template = totalUnits === 1 ? this.localeStrings.unitCountOne : this.localeStrings.unitCountOther;
        const uom = totalUnits === 1 ? this.uomSingular : this.uomPlural;
        unitsEl.textContent = template.replace('[count]', totalUnits).replace('[unit]', uom);
      }
      // A sale is active on the resolved tier when its regular price is higher.
      const onSale = activeRegularPrice > activeUnitPrice;

      if (unitPriceEl) {
        const cents = Math.round(activeUnitPrice * 100);
        unitPriceEl.textContent = this.formatCents(cents);
        unitPriceEl.classList.toggle('card__volume-price-sale', onSale);
      }
      if (regularPriceEl) {
        // Show the struck-through regular price only while a sale is active.
        if (onSale) {
          const regularCents = Math.round(activeRegularPrice * 100);
          regularPriceEl.textContent = this.formatCents(regularCents);
          regularPriceEl.hidden = false;
        } else {
          regularPriceEl.textContent = '';
          regularPriceEl.hidden = true;
        }
      }
      if (priceEl) priceEl.textContent = this.formatMoney(totalPrice);
    });
  
    // Highlight the active tier row
    this.tierRows.forEach((row) => {
      row.classList.toggle('is-active', parseInt(row.dataset.tier, 10) === activeTierIndex);
    });
  }

  // Find the active tier's unit price for the current case count.
  // Mirrors handleQtyChange's tier-finding logic; used by ComboCase ATC
  // to compute bundle totals.
  getActiveUnitPrice(cases) {
    let activeUnitPrice = this.baseUnitPrice;
    this.tierRows.forEach((row) => {
      const minCases = parseInt(row.dataset.minCases, 10);
      if (cases >= minCases) {
        activeUnitPrice = parseFloat(row.dataset.unitPrice);
      }
    });
    return activeUnitPrice;
  }
  
  // Format an amount as money using the theme's currency format string.
  formatMoney(amount) {
    const cents = Math.round(amount * 100);
    return this.formatCents(cents);
  }

  // Format a cents amount using the theme's configured currency format string.
  // Supports common Shopify placeholders: amount, amount_no_decimals, amount_with_comma_separator, etc.
  formatCents(cents) {
    const format = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return `$${(cents / 100).toFixed(2)}`;

    const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number === null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const decimals = parts[1] ? decimal + parts[1] : '';
      return dollars + decimals;
    };

    let value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  async handleAtcClick(evt) {
    evt.preventDefault();

    const clickedBtn = evt.currentTarget;
    if (clickedBtn.getAttribute('aria-disabled') === 'true') return;
    if (!this.qtyInputs.length) return;

    // ComboCase products add the parent variant as a single cart line
    // with bundle metadata (including a JSON array of components); the
    // cart transform expands it into the visible bundle. See
    // handleComboCaseAtc for details.
    if (this.isComboCase) {
      return this.handleComboCaseAtc(clickedBtn);
    }

    const variantId = clickedBtn.dataset.variantId;
    const cases = parseInt(this.qtyInputs[0].value, 10) || 1;
    // The cart charges per unit, so it needs the total number of units,
    // not the number of cases. (The qty stepper and tier table stay in cases for the shopper.)
    const quantity = cases * this.itemsPerCase;

    if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }

    // Clear any previous error before this attempt.
    this.showAtcError(clickedBtn, '');

    // Loading state on the clicked button.
    clickedBtn.setAttribute('aria-disabled', 'true');
    clickedBtn.classList.add('is-loading');
  
    // Sections to render back, mirroring the theme's ProductForm pattern.
    const cartDrawer = document.querySelector('cart-drawer');
    let sections = 'cart-icon-bubble';
    if (cartDrawer) {
      sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
    }
  
    let data;
    // Held back until the line's properties describe what it holds, so a shortfall is never
    // reported over a line that is still mispriced behind the message. Declared out here so the
    // finally can leave the success styling off a partial fill.
    let shortfall = null;

    try {
      // A SKU gets one cart line. Tier properties record the case count of the add that made the
      // line, so a second add can't merge into it server-side; raise that line instead, which also
      // hands the transform the combined case count and the tier it earns. Products without tier
      // properties carry nothing that varies per add, so they merge on their own.
      const { caseUnits } = this;
      const existingLine = this.hasTiers ? await this.findCartLine(variantId) : null;

      if (existingLine) {
        // The quantity goes first and on its own. A case count is only true of a quantity the cart
        // actually took, and the cart can refuse or part-fill this one, so sending both together
        // would leave the line describing a count it never reached and being priced from it.
        const settled = await this.raiseToWholeCases(
          existingLine,
          existingLine.quantity + quantity
        );

        // Counted in cases either way. Shopify's own reason counts units, which reads as a
        // different number than the one on screen and describes stock that giving back the part
        // case has since returned.
        if (settled.trimmed || settled.refusal) {
          shortfall = casesAvailableMessage(settled.cases);
          this.revealStock(settled.cart);
        }

        // Nothing whole is left to hold, so there is no longer a line to re-describe.
        if (settled.cases < 1) throw new Error(shortfall || casesAvailableMessage(0));

        data = await changeCartLine({
          id: existingLine.key,
          // Restated even though settling already left the line here, because change.js assumes
          // a quantity of 1 when none is given. Omitting it empties the line down to a single
          // unit behind the properties write.
          quantity: settled.cases * caseUnits,
          // change.js replaces properties rather than patching them, so the line's own set goes
          // back with the tier properties rewritten for the count it now holds. Rebuilt rather
          // than edited, so a line opened by a path that wrote none still ends up priced by tier.
          properties: {
            ...existingLine.properties,
            ...this.tierProperties(settled.cases)
          },
          sections,
          sections_url: window.location.pathname
        });
      } else {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        this.appendLineItemProperties(formData, cases);
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        data = await response.json();

        // The cart banks whatever stock it can cover and reports the rest, so a refusal can still
        // have written a line, and an accepted add can be left holding part of a case. Either
        // leaves the properties sent with the add claiming cases the line never got, and the
        // transform prices from those, so the line is re-read and re-described from the cart.
        // Judged by what the line holds rather than by what was asked for, because an add with
        // nothing to vary per add merges server-side and comes back describing more than this
        // click contributed.
        const shortAdd = caseUnits > 1 && (!response.ok || data.quantity % caseUnits !== 0);
        const added = shortAdd ? await this.settleAddedLine(variantId, sections) : null;

        if (added) {
          data = added.cart;
          shortfall = casesAvailableMessage(added.cases);
          this.revealStock(added.cart);
        } else if (!response.ok) {
          throw new Error(parseCartApiError(data, response.status));
        }
      }

      // A shortfall still added what the cart could cover, so it takes the success path with its
      // message alongside. The cart is consistent by here, and data is the settled cart.
      if (shortfall) this.showAtcError(clickedBtn, shortfall);

      // A raised or settled line comes back as the whole cart, which caps this card straight away
      // instead of leaving the old ceiling live until the shared re-read lands.
      if (data?.items) this.applyCaseCap(data);

      // Update the cart icon bubble in the header
      this.updateCartIcon(data);
  
      // Render cart drawer contents (if drawer present and theme is configured to open it)
      if (cartDrawer) {
        setTimeout(() => {
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        }, 700);
      } else if (theme.settings.afterAtc === 'page' && !shortfall) {
        // Redirect to cart page if theme is configured that way. Not after a shortfall, whose
        // message would be gone before it could be read.
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      }
  
      // Dispatch event for the theme's cart listeners, which include the case cap on every card.
      // Taken from the click rather than the response, which describes the whole cart when an
      // existing line was raised.
      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { 
          variantId: Number(variantId)
        }
      }));
    } catch (error) {
      console.error(error);
      this.showAtcError(clickedBtn, error.message);

      if (cartDrawer) {
        if (typeof cartDrawer.refresh === 'function') {
          await cartDrawer.refresh(true);
        }
        if (theme.settings.afterAtc === 'drawer' && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      } else {
        // Page mode: refresh the cart icon so the count stays accurate
        // after the add attempt.
        await this.refreshCartIcon();
      }
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
    } finally {
      // Re-enable the clicked button. A partial fill gets no success styling over its message.
      if (!shortfall) clickedBtn.classList.add('is-success');
      clickedBtn.removeAttribute('aria-disabled');
      setTimeout(() => {
        clickedBtn.classList.remove('is-loading');
        clickedBtn.classList.remove('is-success');
      }, 1400);
    }
  }

  // Bundle properties for a combo case at a given case count. Every figure the cart transform
  // prices from is derived here, so a line can be rebuilt for a different count by asking again.
  // Returns null when the card carries no components to bundle.
  buildComboProperties(cases) {
    // Children come from the hidden template populated by Liquid.
    const contentsTemplate = this.querySelector('[data-combo-case-contents]');
    if (!contentsTemplate) return null;

    const contents = contentsTemplate.content.querySelectorAll('[data-combo-content]');
    if (!contents.length) return null;

    const components = [];
    contents.forEach((item) => {
      const childVariantId = parseInt(item.dataset.variantId, 10);
      const quantity = (parseInt(item.dataset.qty, 10) || 1) * cases;
      if (childVariantId && quantity > 0) {
        components.push({ variant_id: childVariantId, quantity, sku: item.dataset.sku || '' });
      }
    });

    if (!components.length) return null;

    // Priced from the active tier, after sale logic.
    const bundleTotalCents = Math.round(this.getActiveUnitPrice(cases) * this.itemsPerCase * cases * 100);

    return {
      _bundle_role: 'parent',
      _bundle_type: 'ComboCase',
      _bundle_cases: String(cases),
      _bundle_title: contentsTemplate.dataset.parentTitle || '',
      _bundle_total_cents: String(bundleTotalCents),
      _bundle_total_units: String(components.reduce((total, c) => total + c.quantity, 0)),
      _bundle_components: JSON.stringify(components)
    };
  }

  // ComboCase ATC: the bundle is a single cart line at quantity 1, described entirely by its
  // _bundle_* properties, which the cart transform expands into the visible components.
  async handleComboCaseAtc(clickedBtn) {
    const parentVariantId = clickedBtn.dataset.variantId;
    const cases = parseInt(this.qtyInputs[0].value, 10) || 1;

    const properties = this.buildComboProperties(cases);
    if (!properties) return;

    if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }

    clickedBtn.setAttribute('aria-disabled', 'true');
    clickedBtn.classList.add('is-loading');

    const cartDrawer = document.querySelector('cart-drawer');

    try {
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // A SKU gets one cart line. Every bundle figure describes a single add, so a second add
      // rebuilds the line for the combined case count rather than stacking a bundle onto a total
      // that no longer covers it.
      const existingLine = await this.findCartLine(parentVariantId, 'parent');
      let data;
      let comboProperties = null;

      if (existingLine) {
        // A quantity above one means an earlier add stacked onto this line, leaving it holding that
        // many bundles of the case count it records.
        const existingCases = (parseInt(existingLine.properties._bundle_cases, 10) || 1)
          * (existingLine.quantity || 1);

        // The properties carry the combined count, so the line goes back to the one bundle a combo
        // line always holds. Stated rather than left out: change.js assumes a quantity of 1 when
        // none is given, and relying on that leaves the count that matters here looking accidental.
        comboProperties = this.buildComboProperties(existingCases + cases);

        data = await changeCartLine({
          id: existingLine.key,
          quantity: 1,
          properties: comboProperties,
          sections,
          sections_url: window.location.pathname
        });
      } else {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: parentVariantId, quantity: 1, properties }],
            sections,
            sections_url: window.location.pathname
          })
        });

        data = await response.json();
        if (!response.ok) throw new Error(parseCartApiError(data, response.status));
      }

      if (comboProperties) data = await settleComboQuantity(data, comboProperties, sections);

      // Update the cart icon bubble in the header.
      this.updateCartIcon(data);

      // Render cart drawer contents (if drawer present and theme is configured to open it).
      if (cartDrawer) {
        setTimeout(() => {
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        }, 700);
      } else if (theme.settings.afterAtc === 'page') {
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: parentVariantId }
      }));
    } catch (error) {
      console.error(error);
      this.showAtcError(clickedBtn, error.message);

      if (cartDrawer) {
        if (typeof cartDrawer.refresh === 'function') {
          await cartDrawer.refresh(true);
        }
        if (theme.settings.afterAtc === 'drawer' && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      } else {
        // Page mode: refresh the cart icon so the count stays accurate
        // after the add attempt.
        await this.refreshCartIcon();
      }
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
    } finally {
      clickedBtn.classList.add('is-success');
      clickedBtn.removeAttribute('aria-disabled');
      setTimeout(() => {
        clickedBtn.classList.remove('is-loading');
        clickedBtn.classList.remove('is-success');
      }, 1400);
    }
  }

  // Describes a line just opened by an add from what the cart actually gave it rather than from
  // what the add asked for: whole cases only, tier properties rewritten for that count, and the
  // sections re-rendered in the same request. Returns null when the add left no line at all, which
  // leaves its refusal to speak for itself.
  async settleAddedLine(variantId, sections) {
    const line = await this.findCartLine(variantId);
    if (!line) return null;

    const { caseUnits } = this;
    const cases = Math.floor(line.quantity / caseUnits);

    const cart = await changeCartLine({
      id: line.key,
      quantity: cases * caseUnits,
      // Not short of a full case, so there is still a line to describe. Rewritten rather than
      // patched, change.js replacing properties outright.
      ...(cases > 0 ? { properties: { ...line.properties, ...this.tierProperties(cases) } } : {}),
      sections,
      sections_url: window.location.pathname
    });

    return { cases, cart };
  }

  // Raises a line to a new quantity and reports the whole cases it ends up holding. No properties
  // are sent, so a refusal costs the line nothing and the caller can write a case count against a
  // quantity that is already real. The cart banks whatever stock it can cover even while reporting
  // a shortfall, so the outcome is read back rather than taken from the reply — which is why this
  // keeps its own fetch rather than using changeCartLine, whose job is to raise a refusal.
  async raiseToWholeCases(line, quantity) {
    const response = await fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: line.key, quantity })
    });

    const data = await response.json();
    const settled = await settleWholeCases(
      line.key,
      this.caseUnits,
      // A refusal describes its reason rather than the cart, so the cart is re-read in that case.
      response.ok ? data : null
    );

    return {
      ...settled,
      refusal: response.ok ? null : parseCartApiError(data, response.status)
    };
  }

  // Finds this variant's existing cart line, so an add can raise it instead of opening a second
  // line for the same SKU. bundleRole picks which kind of line to match: null for a plain one,
  // 'parent' for a combo case's bundle line. Assortment components are never a match — they belong
  // to a bundle the drawer manages.
  async findCartLine(variantId, bundleRole = null) {
    try {
      const cart = await fetchCart();
      const line = cart.items?.find((item) => {
        const properties = item.properties || {};
        if (item.variant_id !== Number(variantId)) return false;
        if (properties._bundle_parent_variant_id) return false;
        return (properties._bundle_role || null) === bundleRole;
      });
      if (!line) return null;

      return { key: line.key, quantity: line.quantity, properties: line.properties || {} };
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // Line item properties for tiered (non-bundle) products. The cart transform reads
  // _type: TieredProduct to apply tier pricing. CustomAssortment products are handled by the
  // assortment drawer, ComboCase products by handleComboCaseAtc.
  tierProperties(cases) {
    if (!this.hasTiers) return null;

    return {
      _type: 'TieredProduct',
      _cases: String(cases),
      _product_url: window.location.pathname
    };
  }

  appendLineItemProperties(formData, cases) {
    const properties = this.tierProperties(cases);
    if (!properties) return;

    Object.entries(properties).forEach(([key, value]) => {
      formData.append(`properties[${key}]`, value);
    });
  }

  async handleAssortmentClick(evt) {
    evt.preventDefault();

    const clickedBtn = evt.currentTarget;
    const wasInCart = this.assortmentInCart;

    // This card's idea of the cart is as old as the page it was rendered on, and the bundle may
    // have been added since, in another tab. Ask the server before opening anything.
    if (this.dataset.productUrl) {
      clickedBtn.classList.add('is-loading');
      clickedBtn.setAttribute('aria-disabled', 'true');
      try {
        await this.syncAssortmentCartState();
      } finally {
        // Cleared before the drawer opens, so this doesn't cancel the loading state the drawer
        // puts on its opener.
        clickedBtn.classList.remove('is-loading');
        clickedBtn.removeAttribute('aria-disabled');
      }
    }

    // The button meant "choose" when they clicked it and means "edit" now. Show them the warning
    // and the new label rather than opening a drawer they didn't ask for; their next click edits.
    if (!wasInCart && this.assortmentInCart) {
      this.assortmentWarnings.forEach((el) => { el.hidden = false; });
      return;
    }

    const inCart = this.assortmentInCart;
    // Editing replaces the cart line, so its case count is what the drawer opens with. The
    // stepper is disabled in that state and cannot be the source.
    const quantity = inCart
      ? this.assortmentCases
      : (this.qtyInputs.length ? parseInt(this.qtyInputs[0].value, 10) || 1 : 1);

    document.dispatchEvent(new CustomEvent('dispatch:assortment-drawer:open', {
      detail: {
        mode: inCart ? 'edit' : 'add',
        opener: clickedBtn,
        productHandle: this.dataset.productHandle,
        quantity,
        components: inCart ? this.dataset.assortmentComponents : null
      }
    }));
  }

  // Dismisses the in-cart warning. The button label carries the same message, so nothing is lost by
  // closing it, and a re-render brings it back hidden.
  handleWarningCloseClick(evt) {
    const warning = evt.currentTarget.closest('[data-assortment-in-cart-warning]');
    if (warning) warning.hidden = true;
  }

  // True when this assortment is already in the cart, so the drawer edits it rather than adding.
  get assortmentInCart() {
    return this.isAssortment && !!this.dataset.assortmentLineKey;
  }

  // True where the warning panel mirrors cart state, rather than being raised by a click.
  get warnsWhileInCart() {
    return this.hasAttribute('data-warn-in-cart');
  }

  // Case count of the cart line this card edits.
  get assortmentCases() {
    return parseInt(this.dataset.assortmentCases, 10) || 1;
  }

  // Re-reads the cart line behind this assortment from the server, so the button label, the stepper
  // and the selection the drawer prefills follow the cart. Editing replaces the line each time, so
  // every one of those changes underneath the card.
  async syncAssortmentCartState() {
    const productUrl = this.dataset.productUrl;
    if (!this.isAssortment || !productUrl) return;

    try {
      const response = await fetch(`${productUrl}?view=assortment-cart-state`);
      if (!response.ok) return;

      const [key = '', cases = '', components = ''] = (await response.text()).trim().split('|');

      this.dataset.assortmentLineKey = key;
      this.dataset.assortmentCases = cases;
      this.dataset.assortmentComponents = components;
      this.applyAssortmentCartState();
    } catch (err) {
      console.error(err);
    }
  }

  // Switches the button to editing, hands the case quantity over to the cart line, and on the
  // product page shows the in-cart warning. Liquid renders this same state on page load.
  applyAssortmentCartState() {
    const inCart = this.assortmentInCart;

    this.assortmentWarnings.forEach((el) => {
      // Only the product page's warning describes standing cart state. A card's is the answer to a
      // click, so this may hide it but never reveal it.
      if (this.warnsWhileInCart) {
        el.hidden = !inCart;
      } else if (!inCart) {
        el.hidden = true;
      }
    });

    this.assortmentBtns.forEach((btn) => {
      const label = inCart ? btn.dataset.labelEdit : btn.dataset.labelAdd;
      if (label) btn.textContent = label;
    });

    this.qtyInputs.forEach((input) => {
      if (inCart) input.value = this.assortmentCases;
      input.disabled = inCart;

      const qtyWrap = input.closest('.qty-input');
      if (!qtyWrap) return;
      qtyWrap.classList.toggle('qty-input--disabled', inCart);
      qtyWrap.querySelectorAll('.qty-input__btn').forEach((btn) => {
        btn.disabled = inCart;
      });
    });

    this.handleQtyChange();
  }
  
  // Update the header cart icon from a cart response's sections.
  updateCartIcon(data) {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && data && data.sections && data.sections['cart-icon-bubble']) {
      cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
    }
  }

  // Fetch and update the header cart icon. Used in page mode on partial or full errors.
  async refreshCartIcon() {
    try {
      const response = await fetch('?sections=cart-icon-bubble');
      if (!response.ok) return;
      const data = await response.json();
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data['cart-icon-bubble']) {
        cartIconBubble.innerHTML = data['cart-icon-bubble'];
      }
    } catch (err) {
      console.error(err);
    }
  }

  // Show or clear the ATC error message in the card footer. Mirrors the
  // theme's product form .js-form-error behavior.
  showAtcError(clickedBtn, message) {
    const footer = clickedBtn.closest('.card__footer-wrap');
    const errorEl = footer ? footer.querySelector('[data-card-atc-error]') : null;
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  handleShipTimingModalOpen(evt) {
    evt.preventDefault();
    if (!this.shipTimingModal) return;
    this.shipTimingModal.classList.remove('hidden');
    this.shipTimingModal.classList.add('flex');
    this.shipTimingModal.open(this.shipTimingModalTrigger);
  }
}

customElements.define('product-card-volume', ProductCardVolume);